/**
 * @fileoverview Navigation principale (sidebar de gauche).
 * Branche les boutons de filtre de vue sur le viewState et gère le badge
 * compteur des BD prêtées. La toolbar conserve uniquement les contrôles
 * d'affichage (tri, présentation, compteur de résultats).
 */

import * as viewState from './view-state.js';

/**
 * Initialise la sidebar de navigation : écouteurs sur les boutons de filtre/section
 * et synchronisation initiale avec l'état courant.
 */
export function initNavigation() {
    // Liens de collection (data-filter) : basculent filter + section = 'collection'
    for (const link of document.querySelectorAll('.nav-link[data-filter]')) {
        link.addEventListener('click', () => {
            const filter = link.dataset.filter;
            const update = { filter, section: 'collection' };

            if (filter === 'loaned') update.sort = 'loanDate';
            else if (filter === 'all') update.sort = 'recent';

            viewState.set(update);
        });
    }

    // Liens de section (data-section) : basculent uniquement section
    for (const link of document.querySelectorAll('.nav-link[data-section]')) {
        link.addEventListener('click', () => {
            viewState.set({ section: link.dataset.section });
        });
    }

    // Synchronisation : initiale + à chaque changement de viewState
    viewState.subscribe(_syncActiveLink);
    _syncActiveLink(viewState.get());
}

/**
 * Met à jour un badge de compteur dans la sidebar.
 * Ambre si count > 0, gris foncé si count = 0. Toujours visible.
 * @private
 * @param {string} id - ID du span badge.
 * @param {number} count
 */
function _setBadge(id, count) {
    const badge = document.getElementById(id);
    if (!badge) return;

    badge.textContent = String(count);
    badge.classList.toggle('has-count', count > 0);
}

/**
 * Met à jour le badge "Toutes" avec le nombre total de BD dans la collection.
 * @param {number} count
 */
export function updateAllBadge(count) {
    _setBadge('chip-badge-all', count);
}

/**
 * Met à jour le badge "Prêtées" avec le nombre de BD prêtées en cours.
 * @param {number} count
 */
export function updateLoanBadge(count) {
    _setBadge('chip-badge-loaned', count);
}

/**
 * Met à jour le badge "Souhaits" avec le nombre total de souhaits d'achat.
 * @param {number} count
 */
export function updateWishBadge(count) {
    _setBadge('chip-badge-wishlist', count);
}

/**
 * Synchronise l'état visuel actif de tous les liens de navigation avec le viewState.
 * - Liens data-filter : actifs si section === 'collection' ET filter correspond.
 * - Liens data-section : actifs si section correspond.
 * @private
 * @param {{ section: string, filter: string }} state
 */
function _syncActiveLink(state) {
    for (const link of document.querySelectorAll('.nav-link[data-filter]')) {
        const active = state.section === 'collection' && link.dataset.filter === state.filter;
        link.classList.toggle('is-active', active);
        link.setAttribute('aria-pressed', String(active));
    }

    for (const link of document.querySelectorAll('.nav-link[data-section]')) {
        const active = link.dataset.section === state.section;
        link.classList.toggle('is-active', active);
        link.setAttribute('aria-pressed', String(active));
    }
}
